<template>
  <div :class="gridClass">
    <label class="block text-sm font-medium text-gray-700 mb-2">
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <!-- Zone d'upload -->
    <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
      <input
        type="file"
        :accept="accept"
        class="hidden"
        :id="uniqueId"
        @change="handleFileChange"
      />
      <label :for="uniqueId" class="cursor-pointer">
        <i class="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1"></i>
        <p class="text-sm text-gray-600">{{ uploadText }}</p>
        <p class="text-xs text-gray-500 mt-0.5">{{ fileName || 'Aucun fichier choisi' }}</p>
        <p v-if="fileTypes" class="text-xs text-gray-400 mt-0.5">
          Types acceptés : {{ fileTypes }}
        </p>
      </label>
    </div>
    
    <!-- Aperçu du fichier uploadé -->
    <div v-if="modelValue && isImageFile(modelValue)" class="mt-4">
      <div class="bg-gray-50 rounded-lg py-1 px-2 border border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-800">Aperçu du fichier</h4>
          <button 
            @click="removeFile"
            class="text-red-500 hover:text-red-700 text-sm"
          >
            <i class="fas fa-trash mr-1"></i>Supprimer
          </button>
        </div>
        
        <!-- Aperçu de l'image -->
        <div class="relative">
          <img 
            :src="getImagePreview(modelValue)" 
            :alt="modelValue?.name || ''"
            class="w-full max-w-l h-32 object-cover rounded-lg border border-gray-300"
          />
          <button 
            @click="openImageFullscreen"
            class="absolute inset-0 w-full h-full bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center text-white opacity-0 hover:opacity-100"
          >
            <i class="fas fa-search-plus text-lg"></i>
          </button>
        </div>
        
        <!-- Informations du fichier -->
        <div class=" text-sm text-gray-600">
          <p class="mb-1"><span class="font-bold">Nom:</span> {{ modelValue?.name || 'N/A' }}</p>
          <p class="mb-1"><span class="font-bold">Taille:</span> {{ formatFileSize(modelValue?.size) }}</p>
          <p><span class="font-bold">Type:</span> {{ modelValue?.type || 'N/A' }}</p>
        </div>
      </div>
    </div>
    
    <!-- Aperçu du fichier non-image -->
    <div v-else-if="modelValue && !isImageFile(modelValue)" class="mt-4">
      <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-800">Fichier uploadé</h4>
          <button 
            @click="removeFile"
            class="text-red-500 hover:text-red-700 text-sm"
          >
            <i class="fas fa-trash mr-1"></i>Supprimer
          </button>
        </div>
        
        <!-- Icône selon le type de fichier -->
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <i :class="getFileIcon(modelValue.type)" class="text-blue-600 text-xl"></i>
          </div>
          <div class="flex-1 space-y-0.5">
            <p class="font-bold text-gray-900">{{ modelValue?.name || 'N/A' }}</p>
            <p class="text-sm text-gray-600">{{ formatFileSize(modelValue?.size) }}</p>
            <p class="text-xs text-gray-500">{{ modelValue?.type || 'N/A' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { FileUploadFieldProps } from '@/types/sellerRegister';

const props = withDefaults(defineProps<FileUploadFieldProps>(), {
  required: false,
  accept: '.pdf,.jpg,.jpeg,.png',
  uploadText: 'Choisir un fichier',
  fileTypes: 'PDF, JPG, PNG',
  gridClass: ''
});

const emit = defineEmits<{
  'update:modelValue': [file: File | null];
}>();

const fileName = ref('');
const uniqueId = computed(() => `file-upload-${Math.random().toString(36).substr(2, 9)}`);

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  
  if (target.files && target.files[0]) {
    const file = target.files[0];
    fileName.value = file.name;
    emit('update:modelValue', file);
  } else {
    fileName.value = '';
    emit('update:modelValue', null);
  }
};

// Vérifier si le fichier est une image
const isImageFile = (file: File | null): boolean => {
  if (!file) return false
  return file.type.startsWith('image/');
};

// Créer un aperçu de l'image
const getImagePreview = (file: File | null): string => {
  if (!file || !isImageFile(file)) return '';
  return URL.createObjectURL(file);
};

// Formater la taille du fichier
const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Obtenir l'icône selon le type de fichier
const getFileIcon = (mimeType: string | undefined): string => {
  if (!mimeType) return 'fas fa-file';
  if (mimeType.startsWith('image/')) return 'fas fa-image';
  if (mimeType === 'application/pdf') return 'fas fa-file-pdf';
  if (mimeType.startsWith('text/')) return 'fas fa-file-alt';
  if (mimeType.startsWith('audio/')) return 'fas fa-file-audio';
  if (mimeType.startsWith('video/')) return 'fas fa-file-video';
  return 'fas fa-file';
};

// Supprimer le fichier
const removeFile = () => {
  fileName.value = '';
  emit('update:modelValue', null);
  
  // Réinitialiser l'input file pour permettre de re-sélectionner le même fichier
  const fileInput = document.getElementById(uniqueId.value) as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
};

// Ouvrir l'image en plein écran
const openImageFullscreen = () => {
  if (props.modelValue && isImageFile(props.modelValue)) {
    const url = URL.createObjectURL(props.modelValue);
    window.open(url, '_blank');
  }
};

// Initialiser avec la valeur du modèle
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    fileName.value = newValue.name;
  } else {
    fileName.value = '';
  }
}, { immediate: true });
</script>
